import type { IObservableArray } from "mobx";
import type { ModelPropTransform } from "mobx-keystone";
import { standaloneAction } from "mobx-keystone";
import { ImmutableError } from "../error";
import type { TModel } from "./t-model";

const failure = (errMessage: string) => new ImmutableError(errMessage);

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unsafe-declaration-merging
export interface ImmutableDate
  extends Omit<
    Date,
    | "setTime"
    | "setMilliseconds"
    | "setUTCMilliseconds"
    | "setSeconds"
    | "setUTCSeconds"
    | "setMinutes"
    | "setUTCMinutes"
    | "setHours"
    | "setUTCHours"
    | "setDate"
    | "setUTCDate"
    | "setMonth"
    | "setUTCMonth"
    | "setFullYear"
    | "setUTCFullYear"
  > {}

const errMessage = "this Date object is immutable";

/**
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class ImmutableDate extends Date {
  // disable mutable methods

  setTime(): any {
    throw failure(errMessage);
  }

  setMilliseconds(): any {
    throw failure(errMessage);
  }

  setUTCMilliseconds(): any {
    throw failure(errMessage);
  }

  setSeconds(): any {
    throw failure(errMessage);
  }

  setUTCSeconds(): any {
    throw failure(errMessage);
  }

  setMinutes(): any {
    throw failure(errMessage);
  }

  setUTCMinutes(): any {
    throw failure(errMessage);
  }

  setHours(): any {
    throw failure(errMessage);
  }

  setUTCHours(): any {
    throw failure(errMessage);
  }

  setDate(): any {
    throw failure(errMessage);
  }

  setUTCDate(): any {
    throw failure(errMessage);
  }

  setMonth(): any {
    throw failure(errMessage);
  }

  setUTCMonth(): any {
    throw failure(errMessage);
  }

  setFullYear(): any {
    throw failure(errMessage);
  }

  setUTCFullYear(): any {
    throw failure(errMessage);
  }
}

/**
 * A variation of the isoStringToDateTransform that mobx-keystone provides
 *
 * This transform is meant to help in situations in which "incoming" dates happen to be in the form of an ISO string (aka coming from a storage medium) and we want to convert them to a Date object while maintaining the typical string -> Date conversion that mobx-keystone provides.
 *
 * @example
 * ```ts
 * class MyModel extends Model({
 *   invoiceDate: prop<string | Date>().withTransform(txIsoStringToDateTransform()),
 * }) {}
 * ```
 */
export const txIsoStringToDateTransform: () => ModelPropTransform<
  string,
  Date
> = () => ({
  transform({ originalValue, cachedTransformedValue }) {
    return cachedTransformedValue ?? new ImmutableDate(originalValue);
  },
  untransform({ transformedValue, cacheTransformedValue }) {
    if (transformedValue instanceof ImmutableDate) {
      cacheTransformedValue();
    } else if (typeof transformedValue === "string") {
      return new ImmutableDate(transformedValue).toISOString();
    }
    return transformedValue.toISOString();
  },
});

export const buildQueryWindow = (
  cursor: string | undefined,
  limit: number | undefined,
  maxTimeBetweenInvoices: number,
) => {
  const start = cursor ? Number(cursor) : 0;
  const delta = maxTimeBetweenInvoices ?? 0;
  const size = limit ?? 0;

  return {
    start,
    end: start + size * delta,
  };
};

export const getQueryWindow = (
  start: Date,
  delta: number,
  size: number,
): [number, number] => {
  return [start.valueOf(), start.valueOf() + size * delta];
};

export function calculateMaxEntryDelta<T>(
  data: T[],
  key: keyof T,
): {
  delta: number;
  sorted: T[];
} {
  if (data.length < 2) return { delta: 0, sorted: data };

  const getDate = (item: T) => item[key] as unknown as Date;

  const deltas: number[] = [];

  const totalChunks = 20;
  const len = data.length;
  const chunks: T[][] = [];
  const chunkSize = Math.ceil(len / totalChunks);

  const sortedData = data.toSorted(
    (a, b) => getDate(b).valueOf() - getDate(a).valueOf(),
  );

  if (len <= 20) {
    return {
      delta:
        (getDate(sortedData[0]).valueOf() -
          getDate(sortedData[data.length - 1]).valueOf()) /
        len,
      sorted: sortedData,
    };
  }

  for (let i = 0; i < Math.min(len, totalChunks); i += chunkSize) {
    chunks.push(sortedData.slice(i, Math.max(i + chunkSize, i + 2)));
  }

  for (const chunk of chunks) {
    if (chunk.length >= 2) {
      const oldest = chunk[0];
      const newest = chunk[chunk.length - 1];
      deltas.push(
        (getDate(newest).valueOf() - getDate(oldest).valueOf()) / chunk.length,
      );
    }
  }

  return { delta: Math.max(...deltas), sorted: sortedData };
}

export const defaultSortByAction = standaloneAction(
  "tilliX/defaultSortBy",
  <R extends TModel>(
    data: IObservableArray<R>,
    attribute: keyof R & string,
  ) => {
    data.sort((a, b) => {
      const aVal = a[attribute];
      const bVal = b[attribute];

      if (aVal instanceof Date && bVal instanceof Date) {
        return bVal.getTime() - aVal.getTime();
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        return bVal - aVal;
      } else {
        return 0;
      }
    });
  },
);
